import type { ExpandedApprovalItem } from 'types/approvals'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from '@mui/material'
import Paper from 'components/@extended/Paper'
import ComponentDisplay from 'components/ComponentDisplay'
import React from 'react'
import { ComponentType } from 'types/components'

interface TargetTableProps {
  organizationName: string
  items: ExpandedApprovalItem[]
}

const TargetTable: React.FC<TargetTableProps> = ({ organizationName, items }) => {
  const theme = useTheme()

  if (!items || items.length === 0) {
    return <></>
  }

  // Filter target items and flatten into rows with period info
  const targetItems = items
    .filter((item) => item.approvalType === 'target')
    .filter((item) => item.targets != null && item.targets.length > 0)
    .filter((item) => item.components != null && item.components.length > 0)
    .map((item) => ({
      component: item.components![0],
      target: item.targets![0],
      periodLabel: item.periodLabel || 'Unknown Period',
      rootPeriodLabel: item.rootPeriodLabel,
      user: item.user,
    }))

  // Custom sort function for period labels
  const sortPeriodLabels = (a: string, b: string) => {
    const order = [
      'FY',
      'H1',
      'H2',
      'Q1',
      'Q2',
      'Q3',
      'Q4',
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]

    const getOrderIndex = (label: string) => {
      for (let i = 0; i < order.length; i++) {
        if (label.includes(order[i])) {
          return i
        }
      }
      return order.length + label.localeCompare(label) // For alphabetical sorting of other values
    }

    return getOrderIndex(a) - getOrderIndex(b)
  }

  // Sort all items by period and then by component label
  const sortedItems = targetItems.sort((a, b) => {
    const periodCompare = sortPeriodLabels(a.periodLabel, b.periodLabel)
    if (periodCompare !== 0) return periodCompare
    return a.component.label.localeCompare(b.component.label)
  })

  if (sortedItems.length === 0) {
    return <></>
  }

  return (
    <div style={{ width: '100%', marginBottom: '20px' }}>
      <Typography variant="h3" mb={2}>{`${organizationName} Targets`}</Typography>
      <Paper>
        <TableContainer sx={{ maxHeight: 860, minHeight: 200 }}>
          <Table stickyHeader size="small" aria-label="targets table">
            <TableHead
              sx={{
                '& th': {
                  borderTop: `1px solid ${theme.palette.divider}`,
                  borderBottom: `2px solid ${theme.palette.divider} !important`,
                },
              }}
            >
              <TableRow>
                <TableCell sx={{ minWidth: 200 }}>Target</TableCell>
                <TableCell align="center" sx={{ minWidth: 120 }}>
                  Period
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 150 }}>
                  Owner
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 150 }}>
                  Value
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedItems.map((item, index) => {
                const { component, target, periodLabel, user } = item
                const compositeId = `${component.id}_${target.periodId}`

                // Parse sourceInfo if it's a string
                let sourceInfo = component.sourceInfo
                if (typeof sourceInfo === 'string') {
                  sourceInfo = JSON.parse(sourceInfo)
                }

                const ownerLabel = component.isOrganizationLevel
                  ? organizationName || 'Team'
                  : user?.firstName && user?.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : 'Default'

                return (
                  <TableRow key={compositeId}>
                    <TableCell>{component.label}</TableCell>
                    <TableCell align="center">{periodLabel}</TableCell>
                    <TableCell align="center">{ownerLabel}</TableCell>
                    <TableCell align="center">
                      <ComponentDisplay
                        componentType={component.componentType as ComponentType}
                        value={target.value}
                        sourceType={component.sourceType}
                        compositeFunction={sourceInfo?.compositeFunction}
                        periodLabel={periodLabel}
                        fontSize="14px"
                      />
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  )
}

export default TargetTable
